/** @jsx jsx */
import LazyLoad from 'react-lazyload';
import _ from 'lodash';
import { css, jsx } from '@emotion/core';

export type ImageProps = {
  /** Alternate text of the image. */
  alt?: string;
  /** HTML aria-hidden property. */
  'aria-hidden'?: boolean;
  /** Optional class name. */
  className?: string;
  /** The height of the image in pixels. */
  height?: number;
  /**
   * The offset in pixels at which you wish the image lazyloader to trigger.
   * For example, if this is set to `100`, it will load the image when it is
   * 100px away from appearing at the the bottom of the viewport.
   * Also accepts negative numbers, or an array of numbers which will trigger
   * both the top-edge and bottom-edge offset.
   * If you provide a value of `null` value, you will opt-out of lazy loading.
   */
  // should be using LazyLoadProps['offset'] from import { LazyLoadProps } from 'react-lazyload';
  lazyloadOffset?: number | number[];
  /**
   * Enables 300ms throttling of the lazy-loading mechanism.
   * see https://github.com/twobin/react-lazyload#debouncethrottle
   */
  lazyloadThrottle?: boolean;
  /** Callback for when img fails to load */
  onError?: () => void;
  /** URL of the image. */
  src: string;
  /** The width of the image in pixels. */
  width?: number;
};

const imageCss = css({
  maxWidth: '100%',
});

/**
 * An image.
 */
const Image = ({
  alt,
  lazyloadOffset,
  lazyloadThrottle,
  ...props
}: ImageProps) => {
  const image = (
    <img
      alt={alt}
      css={imageCss}
      {..._.pick(props, [
        'alt',
        'aria-hidden',
        'className',
        'height',
        'onError',
        'src',
        'width',
      ])}
    />
  );

  // Users is opting out if `lazyloadOffset` is null so just render image
  return !lazyloadOffset !== null ? (
    image
  ) : (
    <LazyLoad
      height={props.height}
      offset={lazyloadOffset}
      once={true}
      throttle={lazyloadThrottle}
    >
      {image}
    </LazyLoad>
  );
};

Image.defaultProps = {
  alt: '',
  lazyloadOffset: 200,
  lazyloadThrottle: false,
};
export default Image;
